import { Typography } from "@mui/material";
import { theme } from "@talkouttech/portal-shared";
import { ReactComponent as CalendarIcon } from "assets/menu_calendar.svg";
import { ReactComponent as ImportantIcon } from "assets/ImportantIconNotifications.svg";
import { ReactComponent as KudosIcon } from "assets/Kudos.svg";
import { ReactComponent as ReviewsIcon } from "assets/menu_reviews_mono.svg";
import { ReactComponent as ShareIcon } from "assets/Share.svg";
import { NotificationCard } from "components/Notifications/NotificationsCard";
import {
  CalendarAvatarBadge,
  NotificationGroupAvatar,
  SmallAvatarBadge
} from "components/Notifications/NotificationsCard/NotificationCard.styles";
import {
  NotificationGroupKudosMessage,
  NotificationsBadgePostMessage,
  NotificationsPostMessage
} from "components/Notifications/NotificationsMessageSection";
import { NotificationCourseApprovalRequestMessage } from "components/Notifications/NotificationsMessageSection/NotificationsMessageSection";
import { ConfiguredReaction } from "domain/features";
import {
  BaseMetaData,
  CourseAssignemtnsMetaData,
  CourseInstanceApprovalRequestMetaData,
  EarnedBadgeMetaData,
  EventMetaData,
  FollowersPostAudienceMetaData,
  GroupInvitationMetaData,
  KnowledgeRequestMetaData,
  KudosPostMetaData,
  MandatoryPostMetaData,
  ModuleCompleteMetaData,
  NotificationData,
  NotificationLinkType,
  NotificationMetaData,
  NotificationStatus,
  NotificationType,
  PostAudienceMetaData,
  PostCommentMetaData,
  PostCommentReactionMetaData,
  PostMentionedMetaData,
  PostMetaData,
  PostReactionMetaData,
  PostSharedMetaData,
  ReviewMetaData
} from "domain/notifications";
import { ReactionEmoji } from "presentation/NotificationsCenter/components/NotificationsSection/NotificationsSection.styles";
import { ReactNode } from "react";
import { TFunction, Trans } from "react-i18next";
import { MdGroupAdd, MdMessage } from "react-icons/md";
import { mapReactionToKey } from "utils/post-utils";

const calendarEventReminderNotification = (
  result: NotificationData,
  handleNotificationsNavigation: (linkType: string, id: string, postId?: string) => () => void
) => {
  const isRead = result.status === NotificationStatus.Read ? true : false;
  const metaData = result.metadata as EventMetaData;
  const { id, type, _createdOn, updatedOn } = result;

  return (
    <NotificationCard
      key={id}
      read={isRead}
      onClick={handleNotificationsNavigation(type, id, metaData.eventId)}
      notificationBadge={
        <CalendarAvatarBadge>
          <CalendarIcon />
        </CalendarAvatarBadge>
      }
      avatarUrl={""}
      notificationMessage={
        <Typography>
          {metaData.eventName && metaData.formattedDate ? (
            <Trans
              i18nKey={`notifications.${type}`}
              values={{ name: metaData.eventName, date: metaData.formattedDate }}
            >
              Reminder for <span style={{ fontWeight: "bold" }}></span> on {metaData.formattedDate}
            </Trans>
          ) : (
            result.message
          )}
        </Typography>
      }
      _createdOn={_createdOn}
      updatedOn={updatedOn}
    />
  );
};

const reviewNotification = (
  result: NotificationData,
  handleNotificationsNavigation: (linkType: string, id: string, postId?: string) => () => void,
  message: ReactNode
) => {
  const isRead = result.status === NotificationStatus.Read ? true : false;
  const { id, linkType, linkId, _createdOn, updatedOn } = result;

  return (
    <NotificationCard
      key={id}
      read={isRead}
      onClick={handleNotificationsNavigation(linkType, id, linkId)}
      notificationBadge={
        <CalendarAvatarBadge>
          <ReviewsIcon />
        </CalendarAvatarBadge>
      }
      avatarUrl={""}
      notificationMessage={<Typography>{message}</Typography>}
      _createdOn={_createdOn}
      updatedOn={updatedOn}
    />
  );
};

export const notificationsResultFactory = (
  t: TFunction<"translation", undefined>,
  handleNotificationsNavigation: (
    linkType: string,
    id: string,
    postId?: string,
    metadata?: NotificationMetaData
  ) => () => void,
  configReactions?: ConfiguredReaction[]
) => ({
  [NotificationType.Chat]: (result: NotificationData) => {
    return null;
  },
  [NotificationType.Post]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, displayName } = result.metadata as PostMetaData;
    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage displayName={displayName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.MandatoryPost]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, postedByName } = result.metadata as MandatoryPostMetaData;
    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={
          <SmallAvatarBadge>
            <ImportantIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsBadgePostMessage displayName={postedByName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.Course]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { courseName } = result.metadata as CourseAssignemtnsMetaData;
    const { id, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={
          <SmallAvatarBadge>
            <ImportantIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            <Trans i18nKey="notifications.courseAssigned" values={{ courseName: courseName ?? "" }}>
              You have been assigned the course
              <span style={{ fontWeight: "bold" }}></span>
            </Trans>
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CourseReAssigned]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { courseName } = result.metadata as CourseAssignemtnsMetaData;
    const { id, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(NotificationLinkType.Course, id, linkId)}
        notificationBadge={
          <SmallAvatarBadge>
            <ImportantIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            <Trans i18nKey="notifications.courseReAssigned" values={{ courseName: courseName ?? "" }}>
              Your assigned course <span style={{ fontWeight: "bold" }}></span>, has been updated, tap to complete it
            </Trans>
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.PostMentioned]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, displayName, postId } = result.metadata as PostMentionedMetaData;
    const { id, type, linkType, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, postId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage displayName={displayName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.PostMentionedAdded]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, displayName, postId } = result.metadata as PostMentionedMetaData;
    const { id, type, linkType, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, postId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage displayName={displayName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.PostAudience]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, postedByName, audienceName } = result.metadata as PostAudienceMetaData;
    const isKnowledgeRequest = (result.metadata as BaseMetaData).postType === "knowledgeRequest";
    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationGroupKudosMessage
            displayName={postedByName ?? ""}
            message={isKnowledgeRequest ? t("notifications.krnew") : t(`notifications.${type}`)}
            receiverName={audienceName ?? ""}
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.FollowersAudience]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, postedByName } = result.metadata as FollowersPostAudienceMetaData;
    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsBadgePostMessage displayName={postedByName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.PostComment]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;

    const isCommentReply = result.subtype !== undefined && result.subtype === "reply";

    const isKnowledgeRequest = (result.metadata as BaseMetaData).postType === "knowledgeRequest";
    let metaData;

    if (isKnowledgeRequest) {
      metaData = result.metadata as KnowledgeRequestMetaData;
    } else {
      metaData = result.metadata as PostCommentMetaData;
    }

    const { id, type, linkType, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, metaData.postId)}
        backgroundColor={theme.colors.success}
        notificationBadge={
          !isKnowledgeRequest && (
            <SmallAvatarBadge>
              <MdMessage size={12} />
            </SmallAvatarBadge>
          )
        }
        avatarUrl={metaData.avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage
            displayName={metaData.postedByName ?? ""}
            message={
              isKnowledgeRequest
                ? isCommentReply || (metaData as KnowledgeRequestMetaData).replyToId
                  ? t("notifications.krRepliesForAnswer")
                  : t("notifications.krAnsweredYourRequest")
                : t(`notifications.${type}`)
            }
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.GroupInvite]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, invitedByName, displayName, invitedByAvatar, postId } =
      result.metadata as GroupInvitationMetaData;

    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={
          <SmallAvatarBadge>
            <MdGroupAdd size={12} color={theme.colors.black} />
          </SmallAvatarBadge>
        }
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationGroupKudosMessage
            displayName={invitedByName ?? ""}
            message={t(`notifications.${type}`)}
            receiverName={displayName ?? ""}
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
        rightComponent={<NotificationGroupAvatar src={invitedByAvatar ?? ""} alt={postId ?? ""} variant={"rounded"} />}
      />
    );
  },
  [NotificationType.PostShared]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, displayName } = result.metadata as PostSharedMetaData;

    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        backgroundColor={theme.colors.purple}
        notificationBadge={
          <SmallAvatarBadge>
            <ShareIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage displayName={displayName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.KudosPost]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { avatarUrl, postedByName, kudosName } = result.metadata as KudosPostMetaData;

    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        backgroundColor={theme.colors.white}
        notificationBadge={
          <SmallAvatarBadge>
            <KudosIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationGroupKudosMessage
            displayName={postedByName ?? ""}
            message={t(`notifications.${type}`)}
            receiverName={kudosName ?? ""}
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.PostCommentReaction]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const isKnowledgeRequest = (result.metadata as BaseMetaData).postType === "knowledgeRequest";
    let metaData;

    if (isKnowledgeRequest) {
      metaData = result.metadata as KnowledgeRequestMetaData;
    } else {
      metaData = result.metadata as PostCommentReactionMetaData;
    }

    const { id, type, linkType, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, metaData.postId)}
        backgroundColor={theme.colors.border}
        notificationBadge={
          configReactions && !isKnowledgeRequest ? (
            <SmallAvatarBadge>
              <ReactionEmoji>{mapReactionToKey(metaData.reactionKey, configReactions)?.emote}</ReactionEmoji>
            </SmallAvatarBadge>
          ) : null
        }
        avatarUrl={metaData.avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage
            displayName={metaData.displayName ?? ""}
            message={
              isKnowledgeRequest
                ? (metaData.otherUserCount || 0) > 0
                  ? t("notifications.krManyUpvotedAnswer", { number: metaData.otherUserCount })
                  : t("notifications.krUpvotedAnswer")
                : t(`notifications.${type}`)
            }
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.PostReaction]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const isKnowledgeRequest = (result.metadata as BaseMetaData).postType === "knowledgeRequest";
    let metaData;

    if (isKnowledgeRequest) {
      metaData = result.metadata as KnowledgeRequestMetaData;
    } else {
      metaData = result.metadata as PostReactionMetaData;
    }

    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        backgroundColor={theme.colors.border}
        notificationBadge={
          configReactions && !isKnowledgeRequest ? (
            <SmallAvatarBadge>
              <ReactionEmoji>{mapReactionToKey(metaData.reactionKey, configReactions)?.emote}</ReactionEmoji>
            </SmallAvatarBadge>
          ) : null
        }
        avatarUrl={metaData.avatarUrl ?? ""}
        notificationMessage={
          <NotificationsPostMessage
            displayName={metaData.displayName ?? ""}
            message={
              isKnowledgeRequest
                ? (metaData.otherUserCount || 0) > 0
                  ? t("notifications.krManyUpvotedYourRequest", { number: metaData.otherUserCount })
                  : t("notifications.krUpvotedYourRequest")
                : t(`notifications.${type}`)
            }
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.BadgeEarned]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { imageUrl, badgeName } = result.metadata as EarnedBadgeMetaData;
    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId, result.metadata)}
        notificationBadge={null}
        avatarUrl={imageUrl ?? ""}
        notificationMessage={
          <NotificationsBadgePostMessage displayName={badgeName ?? ""} message={t(`notifications.${type}`)} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.Answer]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { question, avatarUrl } = result.metadata as PostMetaData;

    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsBadgePostMessage displayName={""} message={t(`notifications.${type}`, { question })} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.Reminders]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { question, avatarUrl } = result.metadata as PostMetaData;

    const { id, type, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        // Use type here - custom handling
        onClick={handleNotificationsNavigation(type, id, linkId)}
        notificationBadge={null}
        avatarUrl={avatarUrl ?? ""}
        notificationMessage={
          <NotificationsBadgePostMessage displayName={""} message={t(`notifications.${type}`, { question })} />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CourseInstanceApprovalRequest]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const { courseName, userDisplayName } = result.metadata as CourseInstanceApprovalRequestMetaData;

    const { id, linkId, _createdOn, updatedOn } = result;
    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(NotificationType.CourseInstanceApprovalRequest, id, linkId)}
        notificationBadge={
          <SmallAvatarBadge>
            <ImportantIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <NotificationCourseApprovalRequestMessage
            url={linkId}
            message={t("notifications.courseInstanceApprovalRequest", { name: userDisplayName, courseName })}
          />
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CalendarEventEnrolmentAssigned]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const metaData = result.metadata as EventMetaData;
    const { id, type, _createdOn, updatedOn } = result;

    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(type, id, metaData.eventId)}
        notificationBadge={
          <CalendarAvatarBadge>
            <CalendarIcon />
          </CalendarAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            {metaData.eventName && metaData.formattedDate ? (
              <Trans
                i18nKey={`notifications.${type}`}
                values={{ name: metaData.eventName, date: metaData.formattedDate }}
              >
                You have been enrolled on <span style={{ fontWeight: "bold" }}></span> on {metaData.formattedDate}
              </Trans>
            ) : (
              result.message
            )}
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CalendarEventEnrolmentCancelled]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const metaData = result.metadata as EventMetaData;
    const { id, type, _createdOn, updatedOn } = result;

    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(type, id, metaData.eventId)}
        notificationBadge={
          <CalendarAvatarBadge>
            <CalendarIcon />
          </CalendarAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            {metaData.eventName && metaData.formattedDate ? (
              <Trans
                i18nKey={`notifications.${type}`}
                values={{ name: metaData.eventName, date: metaData.formattedDate }}
              >
                Your enrolment on <span style={{ fontWeight: "bold" }}></span> on {metaData.formattedDate} was cancelled
              </Trans>
            ) : (
              result.message
            )}
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CalendarEventCancelled]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const metaData = result.metadata as EventMetaData;
    const { id, type, _createdOn, updatedOn } = result;

    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(type, id, metaData.eventId)}
        notificationBadge={
          <CalendarAvatarBadge>
            <CalendarIcon />
          </CalendarAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            {metaData.eventName && metaData.formattedDate ? (
              <Trans
                i18nKey={`notifications.${type}`}
                values={{ name: metaData.eventName, date: metaData.formattedDate }}
              >
                {""}
                <span style={{ fontWeight: "bold" }}></span> on {metaData.formattedDate} has been cancelled
              </Trans>
            ) : (
              result.message
            )}
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CalendarEventUpdated]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const metaData = result.metadata as EventMetaData;
    const { id, type, _createdOn, updatedOn } = result;

    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(type, id, metaData.eventId)}
        notificationBadge={
          <CalendarAvatarBadge>
            <CalendarIcon />
          </CalendarAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            {metaData.eventName && metaData.formattedDate ? (
              <Trans
                i18nKey={`notifications.${type}`}
                values={{ name: metaData.eventName, date: metaData.formattedDate }}
              >
                {""}
                <span style={{ fontWeight: "bold" }}></span> on {metaData.formattedDate} has been updated, tap to view details
              </Trans>
            ) : (
              result.message
            )}
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.CalendarEventReminderDayBefore]: (result: NotificationData) =>
    calendarEventReminderNotification(result, handleNotificationsNavigation),
  [NotificationType.CalendarEventReminderSameDay]: (result: NotificationData) =>
    calendarEventReminderNotification(result, handleNotificationsNavigation),
  [NotificationType.ModuleComplete]: (result: NotificationData) => {
    const isRead = result.status === NotificationStatus.Read ? true : false;
    const metaData = result.metadata as ModuleCompleteMetaData;
    const { id, type, linkType, linkId, _createdOn, updatedOn } = result;

    return (
      <NotificationCard
        key={id}
        read={isRead}
        onClick={handleNotificationsNavigation(linkType, id, linkId)}
        notificationBadge={
          <SmallAvatarBadge>
            <ImportantIcon />
          </SmallAvatarBadge>
        }
        avatarUrl={""}
        notificationMessage={
          <Typography>
            {metaData.currentModuleName && metaData.nextModuleName && metaData.courseName ? (
              <Trans
                i18nKey={`notifications.${type}`}
                values={{
                  currentModuleName: metaData.currentModuleName,
                  nextModuleName: metaData.nextModuleName,
                  courseName: metaData.courseName
                }}
              >
                <span style={{ fontWeight: "bold" }}></span> is complete, please start{" "}
                <span style={{ fontWeight: "bold" }}></span> in <span style={{ fontWeight: "bold" }}></span>
              </Trans>
            ) : (
              result.message
            )}
          </Typography>
        }
        _createdOn={_createdOn}
        updatedOn={updatedOn}
      />
    );
  },
  [NotificationType.ReviewAssigned]: (result: NotificationData) => {
    const { definitionName } = result.metadata as ReviewMetaData;
    return reviewNotification(
      result,
      handleNotificationsNavigation,
      definitionName ? (
        <Trans i18nKey="notifications.reviewAssigned" values={{ definitionName }}>
          Please complete your <span style={{ fontWeight: "bold" }}></span>
        </Trans>
      ) : (
        result.message
      )
    );
  },
  [NotificationType.ReviewAssignedReviewer]: (result: NotificationData) => {
    const { definitionName, employeeName } = result.metadata as ReviewMetaData;
    return reviewNotification(
      result,
      handleNotificationsNavigation,
      definitionName && employeeName ? (
        <Trans i18nKey="notifications.reviewAssignedReviewer" values={{ definitionName, employeeName }}>
          <span style={{ fontWeight: "bold" }}></span> has been assigned <span style={{ fontWeight: "bold" }}></span>
        </Trans>
      ) : (
        result.message
      )
    );
  },
  [NotificationType.ReviewSignoffRequired]: (result: NotificationData) => {
    const { definitionName, reviewerName } = result.metadata as ReviewMetaData;
    return reviewNotification(
      result,
      handleNotificationsNavigation,
      definitionName && reviewerName ? (
        <Trans i18nKey="notifications.reviewSignoffRequired" values={{ definitionName, reviewerName }}>
          <span style={{ fontWeight: "bold" }}></span> has completed your <span style={{ fontWeight: "bold" }}></span> -
          please review and confirm your acceptance.
        </Trans>
      ) : (
        result.message
      )
    );
  }
});
